import React, { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useInstances, useWorkspaces } from "@/lib/useInstances";
import { useProjects } from "@/lib/useProjects";
import { isUnread, isConnected, getProjectDisplayName, truncate } from "@/modules/dashboard/types";
import { getTimeAgo } from "@/modules/instance/utils";
import type { Instance, Workspace } from "@/server/types";

type UnreadGroup = {
  label: string;
  instances: Instance[];
};

const BellIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="w-5 h-5"
  >
    <path d="M5.85 3.5a.75.75 0 0 0-1.117-1 9.719 9.719 0 0 0-2.348 4.876.75.75 0 0 0 1.479.248A8.219 8.219 0 0 1 5.85 3.5ZM19.267 2.5a.75.75 0 1 0-1.118 1 8.22 8.22 0 0 1 1.987 4.124.75.75 0 0 0 1.48-.248A9.72 9.72 0 0 0 19.266 2.5Z" />
    <path
      fillRule="evenodd"
      d="M12 2.25A6.75 6.75 0 0 0 5.25 9v.75a8.217 8.217 0 0 1-2.119 5.52.75.75 0 0 0 .298 1.206c1.544.57 3.16.99 4.831 1.243a3.75 3.75 0 1 0 7.48 0 24.583 24.583 0 0 0 4.83-1.244.75.75 0 0 0 .298-1.205 8.217 8.217 0 0 1-2.118-5.52V9A6.75 6.75 0 0 0 12 2.25ZM9.75 18c0-.034 0-.067.002-.1a25.05 25.05 0 0 0 4.496 0L14.25 18a2.25 2.25 0 0 1-4.5 0Z"
      clipRule="evenodd"
    />
  </svg>
);

const StatusDot = ({ status }: { status: Instance["status"] }) => (
  <span className="relative w-2 h-2 flex-shrink-0">
    <span
      className={`absolute inset-0 rounded-full ${
        status === "running"
          ? "bg-green-500"
          : status === "error"
            ? "bg-red-500"
            : "bg-zinc-400"
      }`}
    />
    {status === "running" && (
      <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-75" />
    )}
  </span>
);

const buildGroups = (
  instances: Instance[],
  projectMap: Map<string, string>,
  workspaceMap: Map<string, Workspace>
): UnreadGroup[] => {
  const map = new Map<string, UnreadGroup>();
  const sorted = [...instances].sort((a, b) => {
    const aWs = a.workspaceId ? workspaceMap.get(a.workspaceId) : undefined;
    const bWs = b.workspaceId ? workspaceMap.get(b.workspaceId) : undefined;
    return (aWs?.branchName ?? a.name).localeCompare(bWs?.branchName ?? b.name);
  });
  for (const inst of sorted) {
    const ws = inst.workspaceId ? workspaceMap.get(inst.workspaceId) : undefined;
    const key = inst.projectId ?? inst.workspaceId ?? "unknown";
    const label = inst.projectId
      ? (projectMap.get(inst.projectId) ?? "Unknown Project")
      : getProjectDisplayName(ws?.hostPath ?? ws?.workingDir ?? null, inst.name);
    if (!map.has(key)) map.set(key, { label, instances: [] });
    map.get(key)!.instances.push(inst);
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
};

const InstanceList = ({ groups, workspaceMap }: { groups: UnreadGroup[]; workspaceMap: Map<string, Workspace> }) => (
  <>
    {groups.map((group) => (
      <div key={group.label}>
        <div className="px-3 pt-2 pb-1 text-xs font-semibold text-zinc-500 dark:text-zinc-400">
          {group.label}
        </div>
        {group.instances.map((inst) => {
          const ws = inst.workspaceId ? workspaceMap.get(inst.workspaceId) : undefined;
          return (
            <Link
              key={inst.id}
              href={`/instance/${inst.id}`}
              className="block px-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors"
            >
              <div className="flex items-center gap-2">
                <StatusDot status={inst.status} />
                <span className="text-sm text-zinc-700 dark:text-zinc-300 truncate font-medium flex-1">
                  {ws?.branchName ?? inst.name}
                </span>
                {inst.lastActivity && (
                  <span className="text-[10px] text-zinc-400 dark:text-zinc-500 flex-shrink-0">
                    {getTimeAgo(inst.lastActivity)}
                  </span>
                )}
              </div>
              {inst.taskSummary && (
                <div className="mt-0.5 ml-4 text-xs text-zinc-500 dark:text-zinc-400 truncate">
                  {truncate(inst.taskSummary, 60)}
                </div>
              )}
            </Link>
          );
        })}
      </div>
    ))}
  </>
);

export const UnreadNotificationButton = ({
  currentInstanceId,
}: {
  currentInstanceId: string;
}) => {
  const { data: instances = [] } = useInstances();
  const { data: projects = [] } = useProjects();
  const { data: workspaces = [] } = useWorkspaces();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setOpen(false), 200);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const projectMap = useMemo(
    () => new Map(projects.map((p) => [p.id, p.name])),
    [projects]
  );

  const workspaceMap = useMemo(
    () => new Map(workspaces.map((w) => [w.id, w])),
    [workspaces]
  );

  const connectedOthers = useMemo(
    () => instances.filter((i) => i.id !== currentInstanceId && isConnected(i) && !i.done),
    [instances, currentInstanceId]
  );

  const unreadInstances = useMemo(
    () => connectedOthers.filter(isUnread),
    [connectedOthers]
  );

  const readInstances = useMemo(
    () => connectedOthers.filter((i) => !isUnread(i)),
    [connectedOthers]
  );

  const unreadGroups = useMemo(
    () => buildGroups(unreadInstances, projectMap, workspaceMap),
    [unreadInstances, projectMap, workspaceMap]
  );

  const readGroups = useMemo(
    () => buildGroups(readInstances, projectMap, workspaceMap),
    [readInstances, projectMap, workspaceMap]
  );

  const hasUnread = unreadInstances.length > 0;

  if (connectedOthers.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {open && (
        <div className="absolute bottom-full right-0 mb-2 w-64 max-w-[calc(100vw-2rem)] bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg overflow-hidden max-h-80 overflow-y-auto">
          {hasUnread && (
            <>
              <div className="px-3 py-2 text-[10px] font-medium text-zinc-400 uppercase tracking-wide border-b border-zinc-100 dark:border-zinc-700">
                Unread
              </div>
              <InstanceList groups={unreadGroups} workspaceMap={workspaceMap} />
            </>
          )}
          {readInstances.length > 0 && (
            <>
              <div className={`px-3 py-2 text-[10px] font-medium text-zinc-400 uppercase tracking-wide border-b border-zinc-100 dark:border-zinc-700 ${hasUnread ? "border-t" : ""}`}>
                {hasUnread ? "Other instances" : "Instances"}
              </div>
              <InstanceList groups={readGroups} workspaceMap={workspaceMap} />
            </>
          )}
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center justify-center w-12 h-12 rounded-full text-white shadow-lg transition-colors relative ${
          hasUnread ? "bg-blue-600 hover:bg-blue-700" : "bg-zinc-500 hover:bg-zinc-600"
        }`}
        title={hasUnread ? "Unread instances" : "Instances"}
      >
        <BellIcon />
        {hasUnread && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
            {unreadInstances.length}
          </span>
        )}
      </button>
    </div>
  );
};
